import React, {
  useEffect,
  useState,
} from "react";

import {
  ArrowClockwise,
  CheckCircle,
  Note,
  Plus,
  PhoneSlash,
} from "@phosphor-icons/react";

import ActaModal from "@/components/ui/ActaModal";

function ActionButton({
  icon: Icon,
  title,
  description,
  onClick,
  disabled = false,
  testId,
  primary = false,
}) {
  return (
    <button
      type="button"
      disabled={disabled}
      onClick={onClick}
      className={[
        "flex min-h-[64px] w-full items-center gap-3 rounded-xl border px-4 py-3 text-left transition disabled:cursor-wait disabled:opacity-50",
        primary
          ? "border-[var(--do-accent)] bg-[var(--do-accent-soft)]"
          : "border-[var(--do-border)] bg-[var(--do-bg-solid)] hover:bg-[var(--do-surface-soft)]",
      ].join(" ")}
      data-testid={testId}
    >
      <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-[var(--do-bg-2)] text-[var(--do-accent)]">
        <Icon
          size={20}
          weight="bold"
        />
      </span>

      <span className="min-w-0">
        <strong className="block text-sm font-black text-[var(--do-ink)]">
          {title}
        </strong>

        {description ? (
          <span className="mt-0.5 block text-xs leading-5 text-[var(--do-muted)]">
            {description}
          </span>
        ) : null}
      </span>
    </button>
  );
}

export default function CallReturnDialog({
  open,
  contact,
  saving = false,
  onOutcome,
  onFollowup,
  onNewAction,
  onNote,
  onFinish,
  onClose,
  testId = "call-return-dialog",
}) {
  const [outcome, setOutcome] =
    useState("");

  useEffect(() => {
    if (!open) {
      setOutcome("");
    }
  }, [open]);

  const contactName =
    contact?.name ||
    [
      contact?.firstName,
      contact?.lastName,
    ]
      .filter(Boolean)
      .join(" ") ||
    contact?.company ||
    "ce contact";

  const selectOutcome = async (
    value
  ) => {
    const saved =
      await onOutcome?.(value);

    if (saved !== false) {
      setOutcome(value);
    }
  };

  return (
    <ActaModal
      open={open}
      onClose={onClose}
      eyebrow="Appel commercial"
      title={
        outcome
          ? "Que souhaitez-vous faire ?"
          : "Comment s’est passé l’appel ?"
      }
      subtitle={contactName}
      size="md"
      closeDisabled={saving}
      testId={testId}
      bodyClassName="grid gap-3"
    >
      {!outcome ? (
        <>
          <ActionButton
            icon={PhoneSlash}
            title="Pas de réponse"
            description="L’appel est enregistré. Vous pourrez prévoir une relance."
            disabled={saving}
            onClick={() =>
              selectOutcome("no_answer")
            }
            testId={`${testId}-no-answer`}
          />

          <ActionButton
            icon={CheckCircle}
            title="Réponse"
            description="L’appel est enregistré. Vous pourrez créer une action ou écrire une note."
            disabled={saving}
            onClick={() =>
              selectOutcome("answered")
            }
            testId={`${testId}-answered`}
            primary
          />
        </>
      ) : outcome === "no_answer" ? (
        <>
          <ActionButton
            icon={ArrowClockwise}
            title="Prévoir une relance"
            description="Ouvrir la relance avec ce contact présélectionné."
            onClick={onFollowup}
            testId={`${testId}-followup`}
            primary
          />

          <button
            type="button"
            onClick={onFinish}
            className="min-h-[48px] rounded-xl border border-[var(--do-border)] bg-[var(--do-bg-solid)] px-4 text-sm font-black text-[var(--do-ink)] hover:bg-[var(--do-surface-soft)]"
            data-testid={`${testId}-finish`}
          >
            Terminer
          </button>
        </>
      ) : (
        <>
          <ActionButton
            icon={Plus}
            title="Nouvelle action"
            description="Créer un rendez-vous ou une relance."
            onClick={onNewAction}
            testId={`${testId}-new-action`}
            primary
          />

          <ActionButton
            icon={Note}
            title="Écrire une note"
            description="Ajouter l’information utile à retenir."
            onClick={onNote}
            testId={`${testId}-note`}
          />

          <button
            type="button"
            onClick={onFinish}
            className="min-h-[48px] rounded-xl border border-[var(--do-border)] bg-[var(--do-bg-solid)] px-4 text-sm font-black text-[var(--do-ink)] hover:bg-[var(--do-surface-soft)]"
            data-testid={`${testId}-finish`}
          >
            Terminer
          </button>
        </>
      )}
    </ActaModal>
  );
}
